import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { toast } from "react-toastify";
import NumberField from "../../components/number-field";
import AutocompleteWithPagination from "../../components/autocomplete-with-pagination";
import { inventoryService } from "../../utils/services/inventoryService";
import { productService } from "../../utils/services/productService";
import { categoryService } from "../../utils/services/categoryService";
import { distinctValuesService } from "../../utils/services/distinct-values.service";
import { wmsService } from "../../utils/services/wmsProvider";
import { formatDateTime } from "../../utils/formatDate";
import { fieldSx } from "../manage-catalog/constants";
import { LOST_SALES_SAFE_PERIOD_OPTIONS } from "../../utils/lostSalesParams";
import { useMetricsUi, SectionHead } from "../../components/metrics/metricsUi";
import { StatCard, cardGridSx } from "./forecastUi";

const parsePositiveIntegerInput = (value) => {
  if (value === "") return null;
  const parsed = Number(value);
  if (!Number.isInteger(parsed) || parsed <= 0) return null;
  return parsed;
};

const num = (v, dp = 0) =>
  v === null || v === undefined || v === "" || Number.isNaN(Number(v))
    ? "—"
    : Number(v).toLocaleString("en-US", {
        minimumFractionDigits: dp,
        maximumFractionDigits: dp,
      });

const productName = (p) =>
  !p ? null : typeof p === "string" ? null : p.name || null;

/**
 * Parameters section for Planning > Forecast.
 *  - view mode: read-only params of the stored run, "Run at" sits top-right
 *  - edit mode: the panel itself becomes an inline editable form (no modal) —
 *    Forecast By + multi-SKU picker + time bucket + periods + lost sales +
 *    promo discount. Warehouse and engine are intentionally not exposed:
 *    each picked SKU row carries its own warehouse, engine stays "kaman".
 */
const ForecastParamsPanel = ({
  run,
  clientId,
  editing,
  onCancel,
  onForecasted,
  onSubmittingChange,
  // True when the active run/selection represents the whole product
  // ("All SKUs"), not one specific SKU — the SKU field is hidden in
  // that case since there's no single SKU to show.
  isAllSkusView,
}) => {
  const ui = useMetricsUi();
  const { colors } = ui;
  const input = run?.input || {};

  const [forecastBy, setForecastBy] = useState("SKU");
  const [skuItems, setSkuItems] = useState([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState(null);
  const [horizonType, setHorizonType] = useState("Monthly");
  const [horizon, setHorizon] = useState(6);

  // Supply-planning overrides — sent with every forecast item.
  const [leadTimeDays, setLeadTimeDays] = useState(21);
  const [safetyStockPct, setSafetyStockPct] = useState(10); // % of on hand
  // Extra days of stock the reorder must cover beyond the lead time.
  const [minCoverageDays, setMinCoverageDays] = useState(0);
  // Floor for each SKU's order; blank = the SKU's own inventory-config MOQ.
  const [minOrderQty, setMinOrderQty] = useState("");

  const [includeLostSales, setIncludeLostSales] = useState(false);
  const [searchWindowDays, setSearchWindowDays] = useState(180);
  const [demandWindowDays, setDemandWindowDays] = useState(null);
  const [dowAdjustmentEnabled, setDowAdjustmentEnabled] = useState(true);

  const [discountCollapsed, setDiscountCollapsed] = useState(true);
  const [discountRange, setDiscountRange] = useState("");
  const [discountValue, setDiscountValue] = useState(0);

  const [submitting, setSubmitting] = useState(false);
  // Per-SKU forecast results (shown after a run when any SKU failed)
  const [resultDialog, setResultDialog] = useState({ open: false, items: [] });

  // Read-only "Forecast Parameters" view — collapsible like Explanation, but
  // open by default since these are the primary at-a-glance values.
  const [paramsOpen, setParamsOpen] = useState(true);

  // Every time edit mode opens, reset the form to defaults — do NOT carry
  // over anything from the last run's stored parameters.
  useEffect(() => {
    if (!editing) return;
    setForecastBy("SKU");
    setSkuItems([]);
    setSelectedWarehouse(null);
    setHorizonType("Monthly");
    setHorizon(6);
    setLeadTimeDays(21);
    setSafetyStockPct(10);
    setMinCoverageDays(0);
    setMinOrderQty("");
    setIncludeLostSales(false);
    setSearchWindowDays(180);
    setDemandWindowDays(null);
    setDowAdjustmentEnabled(true);
    setDiscountValue(0);
    setDiscountRange("");
    setDiscountCollapsed(true);
    setSubmitting(false);
    onSubmittingChange?.(false);
  }, [editing]); // eslint-disable-line react-hooks/exhaustive-deps

  const nearestSafePeriodActive = demandWindowDays != null;
  const invalidSearchWindow =
    includeLostSales &&
    (!Number.isInteger(searchWindowDays) || searchWindowDays <= 0);
  const invalidDemandWindow =
    includeLostSales &&
    nearestSafePeriodActive &&
    (!Number.isInteger(demandWindowDays) || demandWindowDays <= 0);
  const demandExceedsSearch =
    includeLostSales &&
    nearestSafePeriodActive &&
    demandWindowDays > searchWindowDays;
  const invalidLostSales =
    invalidSearchWindow || invalidDemandWindow || demandExceedsSearch;

  const discount = useMemo(() => {
    const n = Number(discountValue);
    return Number.isFinite(n) && n > 0 ? n : 0;
  }, [discountValue]);

  const warehouseId = selectedWarehouse?._id || selectedWarehouse || null;

  const byProduct = forecastBy === "Product";
  const byCategory = forecastBy === "Category";
  const byProductType = forecastBy === "Product Type";
  // Category / Product Type pick a group; on submit it's expanded into the
  // products it contains, each forecast at product level.
  const byGroup = byCategory || byProductType;

  const canSubmit =
    skuItems.length > 0 &&
    skuItems.every((it) =>
      byGroup || byProduct
        ? it?._id || it?.product || it?.name || it?.product_type
        : (it?.variants?.sku || it?.sku) && (it?._id || it?.product),
    ) &&
    Number(horizon) > 0 &&
    !invalidLostSales &&
    !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    onSubmittingChange?.(true);
    try {
      // Supply-planning overrides carried on every forecast item.
      const supplyOverrides = {
        lead_time_days: Number(leadTimeDays) || 0,
        safety_stock_percentage: Number(safetyStockPct) || 0,
        min_coverage_days: Number(minCoverageDays) || 0,
        ...(minOrderQty !== "" && minOrderQty != null
          ? { min_order_qty: Number(minOrderQty) || 0 }
          : {}),
      };

      // Same request-object shape as the inventory screen's forecast call —
      // no extra keys beyond what it sends.
      let items;
      if (byGroup) {
        // One item per selected group value, using inventory's keys:
        // `category` (name) or `product_type`. The backend pulls every SKU
        // under those values and runs a normal SKU-level bulk forecast.
        items = skuItems.map((grp) => ({
          ...(byCategory
            ? { category: grp.name }
            : { product_type: grp.product_type || grp.name }),
          horizon: Number(horizon),
          type: horizonType,
          ...(warehouseId ? { warehouse: warehouseId } : {}),
          discount,
          forecast_by: byCategory ? "category" : "product type",
          client: clientId,
          ...supplyOverrides,
        }));
      } else {
        // SKU mode: items come from the product SKU API (/fetch-sku). Product
        // mode: items come from the product name search (/product); the backend
        // expands each product into its variants. Warehouse comes from the
        // dropdown.
        items = skuItems.map((item) => ({
          sku: byProduct ? undefined : item.variants?.sku || item.sku,
          product: item._id || item.product,
          category: item.category,
          product_type: item.product_type,
          horizon: Number(horizon),
          type: horizonType,
          ...(warehouseId ? { warehouse: warehouseId } : {}),
          discount,
          forecast_by: forecastBy,
          client: item.client_id || item.client || clientId,
          ...supplyOverrides,
        }));
      }

      const payload = {
        items,
        bulk: true,
        forecast_type: "warehouse",
        engine: "kaman",
        ...(includeLostSales && {
          include_lost_sales: true,
          search_window_days: searchWindowDays,
          demand_window_days: demandWindowDays,
          dow_adjustment_enabled: dowAdjustmentEnabled,
        }),
      };
      const res = await inventoryService.bulkForecast(payload);

      const results = Array.isArray(res) ? res : [];
      const successes = results.filter(
        (r) => !r.error && r.data?.forecast_data_id,
      );
      const failures = results.filter((r) => r.error);

      // Any failure → show the per-SKU results dialog (same as the inventory
      // forecast). Successes still flow through so their tabs render.
      if (failures.length) {
        setResultDialog({ open: true, items: results });
      }

      if (successes.length) {
        if (!failures.length) {
          toast.success(`${successes.length} forecast(s) generated`);
        }
        onForecasted?.(successes.map((s) => s.data.forecast_data_id));
      } else if (!failures.length) {
        toast.error("Unable to generate forecast now. Please try again later.");
      }
    } catch (e) {
      console.error("Forecast run failed", e);
      toast.error(
        e?.response?.data?.message ||
          e?.message ||
          "Failed to request forecast",
      );
    } finally {
      setSubmitting(false);
      onSubmittingChange?.(false);
    }
  };

  const pName = productName(input.product);

  const blockSx = {
    border: `1px solid ${colors.border}`,
    borderRadius: "10px",
    backgroundColor: ui.surfaceAlt,
    p: 1.75,
  };

  const runAt = run && (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      <Typography
        sx={{
          fontSize: 10.5,
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          color: colors.textPrimary,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        Run at : {formatDateTime(run.createdAt)}
      </Typography>
      <Button
        size="small"
        onClick={() => setParamsOpen((o) => !o)}
        endIcon={paramsOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        sx={{
          textTransform: "none",
          color: colors.textSecondary,
          fontSize: 12,
          minHeight: 0,
          minWidth: 0,
          lineHeight: 1.4,
          py: 0.25,
          px: 1,
          "& .MuiButton-endIcon": { ml: 0.3 },
          "& .MuiButton-endIcon > svg": { fontSize: 16 },
        }}
      >
        {paramsOpen ? "Hide" : "Show"}
      </Button>
    </Box>
  );

  const actions = (
    <Box sx={{ display: "flex", gap: 1 }}>
      <Button size="small" onClick={onCancel} disabled={submitting}>
        Cancel
      </Button>
      <Button
        size="small"
        variant="contained"
        color="primary"
        startIcon={<AutorenewIcon />}
        onClick={handleSubmit}
        disabled={!canSubmit}
      >
        {submitting ? "Running…" : "Run Forecast"}
      </Button>
    </Box>
  );

  const results = resultDialog.items;
  const failCount = results.filter((r) => r.error).length;
  const okCount = results.length - failCount;

  return (
    <>
    <Box
      sx={{ ...ui.card, p: { xs: "16px", sm: "18px 20px" } }}
      component="section"
    >
      <SectionHead
        title={editing ? "Edit Parameters" : "Forecast Parameters"}
        subtitle={
          editing
            ? byGroup
              ? `Every SKU in the selected ${byCategory ? "category" : "product type"} is forecast in the background.`
              : byProduct
                ? "Each selected product is expanded into its SKUs."
                : "A separate forecast run is generated for each selected SKU."
            : undefined
        }
        right={editing ? actions : runAt}
        sx={{ mb: editing || paramsOpen ? 1.25 : 0 }}
      />

      {editing ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1.75,
            mt: 0.5,
          }}
        >
          {/* Line 1 — Forecast By | Time Bucket | Number of Periods */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
              gap: 1.75,
            }}
          >
            <TextField
              select
              fullWidth
              size="small"
              label="Forecast By"
              value={forecastBy}
              onChange={(e) => {
                setForecastBy(e.target.value);
                setSkuItems([]);
              }}
              disabled={submitting}
              sx={fieldSx}
            >
              <MenuItem value="SKU">SKU</MenuItem>
              <MenuItem value="Product">Product</MenuItem>
              <MenuItem value="Category">Category</MenuItem>
              <MenuItem value="Product Type">Product Type</MenuItem>
            </TextField>

            <TextField
              select
              fullWidth
              size="small"
              label="Time Bucket"
              value={horizonType}
              onChange={(e) => setHorizonType(e.target.value)}
              disabled={submitting}
              sx={fieldSx}
            >
              <MenuItem value="Daily">Daily</MenuItem>
              <MenuItem value="Weekly">Weekly</MenuItem>
              <MenuItem value="Monthly">Monthly</MenuItem>
            </TextField>

            <NumberField
              label="Number of Periods"
              value={horizon}
              onChange={(val) => setHorizon(val)}
              fullWidth
              size="small"
              disabled={submitting}
              placeholder="Enter number of periods"
              style={{ backgroundColor: "var(--bg-paper)" }}
            />
          </Box>

          {/* Line 2 — SKU / Product / Category / Product Type | Warehouse */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "2fr 1fr" },
              gap: 1.75,
            }}
          >
            <AutocompleteWithPagination
              key={forecastBy}
              multiple={!byGroup}
              selectedItem={byGroup ? skuItems[0] || null : skuItems}
              onDataChange={(val) =>
                setSkuItems(
                  byGroup
                    ? val
                      ? [val]
                      : []
                    : Array.isArray(val)
                      ? val
                      : [],
                )
              }
              customLabel={
                byCategory
                  ? "Category"
                  : byProductType
                    ? "Product Type"
                    : byProduct
                      ? "Products (search by name)"
                      : "SKUs"
              }
              optionKey={(item) =>
                byProductType
                  ? item?.product_type || ""
                  : byCategory || byProduct
                    ? item?.name || ""
                    : item?.variants?.sku || item?.sku || ""
              }
              apiEndPoint={(limit, skip, search) => {
                if (byCategory)
                  return categoryService.fetchNames(
                    search,
                    clientId,
                    limit || 15,
                    skip || 0,
                  );
                if (byProductType)
                  return distinctValuesService.fetchColumnValues(
                    "product",
                    "product_type",
                    search,
                    clientId,
                    limit || 15,
                    skip || 0,
                  );
                if (byProduct)
                  return productService.findAll(
                    clientId,
                    search,
                    `$limit=${limit || 15}&$skip=${skip || 0}`,
                  );
                return productService.fetchSKU(
                  clientId,
                  search,
                  limit,
                  skip,
                  [],
                  null,
                );
              }}
              disabled={submitting}
              width="100%"
              inputSx={fieldSx}
            />

            <AutocompleteWithPagination
              selectedItem={selectedWarehouse}
              onDataChange={(wh) => setSelectedWarehouse(wh || null)}
              customLabel="Warehouse (optional)"
              optionKey={(item) =>
                item?.name || item?.warehouse_code || item?.code || ""
              }
              apiEndPoint={(limit, skip, search) =>
                wmsService.fetch(clientId, limit, skip, undefined, search)
              }
              disabled={submitting}
              width="100%"
              inputSx={fieldSx}
            />
          </Box>

          {/* Line 3 — Lead Time | Safety Stock (% of on hand) */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" },
              gap: 1.75,
            }}
          >
            <NumberField
              label="Lead Time (days)"
              value={leadTimeDays}
              onChange={(val) =>
                setLeadTimeDays(val === "" ? "" : Math.max(0, Number(val)))
              }
              inputProps={{ min: 0, step: 1 }}
              fullWidth
              size="small"
              disabled={submitting}
              placeholder="Enter lead time in days"
              style={{ backgroundColor: "var(--bg-paper)" }}
            />
            <NumberField
              label="Safety Stock (% of on hand)"
              value={safetyStockPct}
              onChange={(val) =>
                setSafetyStockPct(val === "" ? "" : Math.max(0, Number(val)))
              }
              inputProps={{ min: 0, step: 1 }}
              fullWidth
              size="small"
              disabled={submitting}
              placeholder="Enter safety stock %"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end" sx={{ fontWeight: 600 }}>
                    %
                  </InputAdornment>
                ),
              }}
              style={{ backgroundColor: "var(--bg-paper)" }}
            />
          </Box>

          {/* Line 4 — Minimum coverage window | Minimum order quantity */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" },
              gap: 1.75,
            }}
          >
            <NumberField
              label="Minimum Coverage (days)"
              value={minCoverageDays}
              onChange={(val) =>
                setMinCoverageDays(val === "" ? "" : Math.max(0, Number(val)))
              }
              inputProps={{ min: 0, step: 1 }}
              fullWidth
              size="small"
              disabled={submitting}
              placeholder="Extra days of stock beyond lead time"
              style={{ backgroundColor: "var(--bg-paper)" }}
            />
            <NumberField
              label="Min Order Qty (per SKU)"
              value={minOrderQty}
              onChange={(val) =>
                setMinOrderQty(val === "" ? "" : Math.max(0, Number(val)))
              }
              inputProps={{ min: 0, step: 1 }}
              fullWidth
              size="small"
              disabled={submitting}
              placeholder="Blank = each SKU's own MOQ"
              style={{ backgroundColor: "var(--bg-paper)" }}
            />
          </Box>

          {/* Lost Sales */}
          <Box
            sx={{
              ...blockSx,
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: 13,
                  color: colors.textPrimary,
                }}
              >
                Include Lost Sales
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={includeLostSales}
                    onChange={(e) => setIncludeLostSales(e.target.checked)}
                    disabled={submitting}
                  />
                }
                label={includeLostSales ? "On" : "Off"}
                labelPlacement="start"
                sx={{ mr: 0, "& .MuiFormControlLabel-label": { fontSize: 12 } }}
              />
            </Box>
            {includeLostSales && (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: 1.75,
                }}
              >
                <TextField
                  label="Search Window (days)"
                  type="number"
                  size="small"
                  value={searchWindowDays}
                  onChange={(e) => {
                    const parsed = parsePositiveIntegerInput(e.target.value);
                    if (parsed !== null) setSearchWindowDays(parsed);
                  }}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: 1, step: 1 }}
                  fullWidth
                  disabled={submitting}
                  error={invalidSearchWindow}
                  helperText={
                    invalidSearchWindow
                      ? "Enter a positive whole number of days."
                      : ""
                  }
                  sx={fieldSx}
                />
                <FormControl
                  fullWidth
                  size="small"
                  disabled={submitting}
                  error={invalidDemandWindow || demandExceedsSearch}
                  sx={fieldSx}
                >
                  <InputLabel id="pf-safe-period-label" shrink>
                    Nearest Safe Period
                  </InputLabel>
                  <Select
                    labelId="pf-safe-period-label"
                    label="Nearest Safe Period"
                    value={demandWindowDays ?? ""}
                    displayEmpty
                    onChange={(e) =>
                      setDemandWindowDays(
                        e.target.value === "" ? null : Number(e.target.value),
                      )
                    }
                  >
                    <MenuItem value="">Inactive</MenuItem>
                    {LOST_SALES_SAFE_PERIOD_OPTIONS.map((days) => (
                      <MenuItem key={days} value={days}>
                        {days} days
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    {invalidDemandWindow
                      ? "Choose Inactive or a positive whole number of days."
                      : demandExceedsSearch
                        ? "Must not exceed the search window."
                        : nearestSafePeriodActive
                          ? "Find a safe period of N days within the search window."
                          : "Use the search window and exclude stockout days."}
                  </FormHelperText>
                </FormControl>
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={dowAdjustmentEnabled}
                      onChange={(e) =>
                        setDowAdjustmentEnabled(e.target.checked)
                      }
                      disabled={submitting}
                    />
                  }
                  label="DOW Adjustment"
                  sx={{
                    ml: 0,
                    "& .MuiFormControlLabel-label": { fontSize: 12 },
                  }}
                />
              </Box>
            )}
          </Box>

          {/* Promo / Discount */}
          <Box>
            <Box
              onClick={() => !submitting && setDiscountCollapsed((c) => !c)}
              sx={{
                ...blockSx,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                cursor: submitting ? "default" : "pointer",
                opacity: submitting ? 0.6 : 1,
              }}
            >
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: 13,
                  color: colors.textPrimary,
                }}
              >
                Promo / Campaign Discount
                {discount ? (
                  <Box
                    component="span"
                    sx={{
                      ml: 1,
                      fontSize: 11,
                      fontWeight: 700,
                      color: ui.accent,
                      backgroundColor: ui.accentSoft,
                      borderRadius: "20px",
                      px: 1,
                      py: 0.2,
                    }}
                  >
                    {discount}%
                  </Box>
                ) : null}
              </Typography>
              {discountCollapsed ? (
                <ExpandMoreIcon sx={{ color: colors.textSecondary }} />
              ) : (
                <ExpandLessIcon sx={{ color: colors.textSecondary }} />
              )}
            </Box>
            {!discountCollapsed && (
              <Box
                sx={{
                  mt: 1.5,
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: 1.75,
                }}
              >
                <FormControl fullWidth size="small" disabled={submitting}>
                  <InputLabel id="pf-discount-label">
                    Select Discount
                  </InputLabel>
                  <Select
                    labelId="pf-discount-label"
                    label="Select Discount"
                    value={discountRange}
                    onChange={(e) => {
                      const v = e.target.value;
                      setDiscountRange(v);
                      if (v !== "custom") setDiscountValue(Number(v) || 0);
                      else setDiscountValue("");
                    }}
                    style={{ backgroundColor: "var(--bg-paper)" }}
                  >
                    {Array.from({ length: 10 }, (_, i) => (
                      <MenuItem key={i} value={`${(i + 1) * 5}`}>
                        {(i + 1) * 5}%
                      </MenuItem>
                    ))}
                    <MenuItem value="custom">Custom</MenuItem>
                  </Select>
                </FormControl>
                {discountRange === "custom" && (
                  <NumberField
                    value={discountValue}
                    onChange={(val) =>
                      setDiscountValue(
                        val === "" ? "" : Math.max(0, Number(val)),
                      )
                    }
                    inputProps={{ min: 0 }}
                    size="small"
                    disabled={submitting}
                    placeholder="Enter discount %"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end" sx={{ fontWeight: 600 }}>
                          %
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              </Box>
            )}
          </Box>
        </Box>
      ) : (
        <Collapse in={paramsOpen} unmountOnExit>
          <Box sx={{ ...cardGridSx, mt: 0.5 }}>
            {!isAllSkusView && (
              <StatCard label="SKU" value={run?.sku || input.sku} />
            )}
            <StatCard label="Forecast By" value={input.forecast_by || "SKU"} />
            {pName && <StatCard label="Product" value={pName} />}
            <StatCard label="Time Bucket" value={input.type} />
            <StatCard label="Periods" value={num(input.horizon)} />
            <StatCard
              label="Discount"
              value={input.discount ? `${num(input.discount)}%` : "None"}
            />
            {input.category && (
              <StatCard label="Category" value={input.category} />
            )}
            {input.lead_time_days != null && (
              <StatCard
                label="Lead Time (days)"
                value={num(input.lead_time_days)}
              />
            )}
            {input.safety_stock_percentage != null && (
              <StatCard
                label="Safety Stock (% of on hand)"
                value={`${num(input.safety_stock_percentage)}%`}
              />
            )}
            {/* Only runs made after these parameters existed carry them. */}
            {input.min_coverage_days != null && (
              <StatCard
                label="Minimum Coverage (days)"
                value={num(input.min_coverage_days)}
              />
            )}
            {input.min_coverage_days != null && (
              <StatCard
                label="Min Order Qty (per SKU)"
                value={
                  input.min_order_qty != null
                    ? num(input.min_order_qty)
                    : "SKU default"
                }
              />
            )}
          </Box>
        </Collapse>
      )}
    </Box>

    {/* Per-SKU forecast results — shown when a run had any failures */}
    <Dialog
      open={resultDialog.open}
      onClose={() => setResultDialog((p) => ({ ...p, open: false }))}
      PaperProps={{ sx: { width: { xs: "92vw", sm: 520 }, borderRadius: 2 } }}
    >
      <DialogTitle>Forecast Results</DialogTitle>
      <DialogContent
        sx={{ display: "flex", flexDirection: "column", gap: 1.5, pt: 1 }}
      >
        {okCount > 0 && failCount > 0 && (
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Chip
              size="small"
              icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
              label={`${okCount} succeeded`}
              sx={{
                backgroundColor: "#DCFCE7",
                color: "#166534",
                "& .MuiChip-icon": { color: "#16A34A" },
              }}
            />
            <Chip
              size="small"
              icon={<ErrorOutlineIcon sx={{ fontSize: 14 }} />}
              label={`${failCount} failed`}
              sx={{
                backgroundColor: "#FEE2E2",
                color: "#991B1B",
                "& .MuiChip-icon": { color: "#DC2626" },
              }}
            />
          </Box>
        )}
        {results.map((item, i) => {
          const sku = item.sku || item.data?.sku;
          return item.error ? (
            <Box
              key={`err-${sku || i}`}
              sx={{
                p: 2,
                border: "1px solid #FCA5A5",
                borderRadius: "12px",
                backgroundColor: "#FEF2F2",
                display: "flex",
                alignItems: "flex-start",
                gap: 1.5,
              }}
            >
              <ErrorOutlineIcon
                sx={{ color: "#DC2626", flexShrink: 0, mt: "2px", fontSize: 18 }}
              />
              <Box>
                {sku && (
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: "#991B1B",
                      mb: 0.3,
                    }}
                  >
                    SKU: {sku}
                  </Typography>
                )}
                <Typography
                  sx={{ fontSize: 12, color: "#DC2626", lineHeight: 1.4 }}
                >
                  {item.message || "Forecast failed for this SKU"}
                </Typography>
              </Box>
            </Box>
          ) : (
            <Box
              key={`ok-${sku || i}`}
              sx={{
                p: 2,
                border: "1px solid #BBF7D0",
                borderRadius: "12px",
                backgroundColor: "#F0FDF4",
                display: "flex",
                alignItems: "flex-start",
                gap: 1.5,
              }}
            >
              <CheckCircleIcon
                sx={{ color: "#16A34A", flexShrink: 0, mt: "2px", fontSize: 18 }}
              />
              <Box>
                {sku && (
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: "#166534",
                      mb: 0.3,
                    }}
                  >
                    SKU: {sku}
                  </Typography>
                )}
                <Typography
                  sx={{ fontSize: 12, color: "#15803D", lineHeight: 1.4 }}
                >
                  {item.message || "Forecast generated"}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={() => setResultDialog((p) => ({ ...p, open: false }))}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default ForecastParamsPanel;
